import { FlightCard, SearchResponse } from '@/src/types/flight'
import { isServer } from '../../browser'
import { isEmpty } from '../../generalUtils'
import { getSplitId } from '../searchUtils/getSplitId'
import { constructSplrt } from './constructSplRT'

const debugMode = false
export const modifyPricesAndSortOnwardLeg = (flightResultResponseData: SearchResponse) => {
    if (debugMode) {
        console.log('modifyPricesAndSortOnwardLeg debug mode is enabled')
    }
    if (flightResultResponseData?.cards?.length < 2) {
        return flightResultResponseData
    }

    const { cards = [], sortData } = flightResultResponseData || {}
    const cheapestReturnCardId = sortData?.price_up[1]?.[0] || ''
    const cheapestReturnCardIndex = Number(getSplitId(cheapestReturnCardId)?.[1]) || 0
    const cheapestReturnCardPrice = cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.pr || 0
    const cheapestReturnCardPriceWODiscount =
        (cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.pr || 0) +
        (cards[1]?.at(cheapestReturnCardIndex)?.priceBreakup?.ds || 0)
    if (Array.isArray(cards[0])) {
        cards[0].forEach((onwardCard, index) => {
            let cheapestPriceForCard = onwardCard.priceBreakup.pr + cheapestReturnCardPrice
            let cheapestPriceForCardWODiscount =
                onwardCard.priceBreakup.pr + onwardCard.priceBreakup.ds + cheapestReturnCardPriceWODiscount
            let cheapestPriceForSpecialRT = cheapestPriceForCard
            let cheapestPriceForSpecialRTWODiscount = cheapestPriceForCardWODiscount

            // Track which return card is used for this onward card (debug code)
            let usedReturnCardId = cheapestReturnCardId // Default to cheapest return card
            let usedReturnCardType = 'cheapest' // Track if using cheapest or special RT
            // End of debug code

            cards[1].forEach((returnCard, returnIndex) => {
                const {
                    isSpecialRT: isOnwawrdSpecialRT,
                    splrtFare: onwardSplrtFare,
                    splrtFareWODiscount: onwardSplrtFareWODiscount
                } = constructSplrt(onwardCard, returnCard.splRtFn) || {}
                const {
                    isSpecialRT: isReturnSpecialRT,
                    splrtFare: returnSplrtFare,
                    splrtFareWODiscount: returnSplrtFareWODiscount
                } = constructSplrt(returnCard, onwardCard.splRtFn) || {}
                const specialRTPrice = (onwardSplrtFare || 0) + (returnSplrtFare || 0)
                const specialRTPriceWODiscount = (onwardSplrtFareWODiscount || 0) + (returnSplrtFareWODiscount || 0)
                if (
                    isOnwawrdSpecialRT &&
                    isReturnSpecialRT &&
                    specialRTPrice < cheapestPriceForSpecialRT &&
                    specialRTPrice < cheapestPriceForCard
                ) {
                    cheapestPriceForSpecialRT = specialRTPrice
                    cheapestPriceForSpecialRTWODiscount = specialRTPriceWODiscount
                    //debug code
                    usedReturnCardId = returnCard.id // Update to special RT return card
                    usedReturnCardType = 'specialRT'
                    // End of debug code
                }
            })

            if (cheapestPriceForSpecialRT < cheapestPriceForCard) {
                onwardCard.priceBreakup.multiAirlinePrice = cheapestPriceForSpecialRT
                onwardCard.priceBreakup.multiAirlinePriceWoDiscount =
                    cheapestPriceForSpecialRTWODiscount > cheapestPriceForSpecialRT
                        ? cheapestPriceForSpecialRTWODiscount
                        : undefined

                const couponDetail = onwardCard?.priceBreakup?.fare?.coupon_detail

                if (couponDetail?.discountAmount) {
                    couponDetail.discountedPrice = cheapestPriceForSpecialRT - couponDetail.discountAmount
                }
            } else {
                onwardCard.priceBreakup.multiAirlinePrice = cheapestPriceForCard
                onwardCard.priceBreakup.multiAirlinePriceWoDiscount =
                    cheapestPriceForCardWODiscount > cheapestPriceForCard ? cheapestPriceForCardWODiscount : undefined

                const couponDetail = onwardCard?.priceBreakup?.fare?.coupon_detail

                if (couponDetail?.discountAmount) {
                    couponDetail.discountedPrice = cheapestPriceForCard - couponDetail.discountAmount
                }
            }

            // Console log which return card is used for this onward card
            if (debugMode) {
                console.log(
                    `🛫 Onward Card: ${onwardCard.id} | Return Card Used: ${usedReturnCardId} | Type: ${usedReturnCardType} | Final Price: ${onwardCard.priceBreakup.multiAirlinePrice}`
                )
            }
        })

        sortData.price_up[0] = cards[0]
            .toSorted((a, b) => {
                return (a.priceBreakup.multiAirlinePrice || 0) - (b.priceBreakup.multiAirlinePrice || 0)
            })
            .map(card => {
                return card.id
            })

        if (debugMode) {
            console.log('after sorting onward cards (cheapest)', sortData.price_up[0])
        }
    }
    return flightResultResponseData
}

export const modifyPricesAndSortOfReturnLeg = (
    flightResultResponseData: SearchResponse,
    selectedOnwardCard: FlightCard
) => {
    if (!isEmpty(selectedOnwardCard) && Array.isArray(flightResultResponseData.cards[1])) {
        if (debugMode) {
            console.log('modifyPricesAndSortOfReturnLeg debug mode is enabled')
        }
        flightResultResponseData.cards[1].forEach(returnCard => {
            const { isSpecialRT: isOnwawrdSpecialRT, splrtFare: onwardSplrtFare } =
                constructSplrt(selectedOnwardCard, returnCard.splRtFn) || {}
            const { isSpecialRT: isReturnSpecialRT, splrtFare: returnSplrtFare } =
                constructSplrt(returnCard, selectedOnwardCard.splRtFn) || {}
            const normalPrice = returnCard.priceBreakup.pr + selectedOnwardCard.priceBreakup.pr
            let totalPrice = 0
            if (isOnwawrdSpecialRT && isReturnSpecialRT) {
                const specialRTPrice = (onwardSplrtFare || 0) + (returnSplrtFare || 0)
                const isNormalPrCheaper = normalPrice < specialRTPrice
                totalPrice = isNormalPrCheaper ? normalPrice : specialRTPrice
                if (debugMode) {
                    console.group(
                        `return card : ${returnCard?.id} | totalPrice(${isNormalPrCheaper ? 'OW_pr+RT_pr' : 'OW_splrt+RT_splrt'}): ${totalPrice} | isSpecialRTPrice: ${!isNormalPrCheaper} | OW_pr+RT_pr: ${normalPrice} | OW_splrt+RT_splrt: ${specialRTPrice}`
                    )
                }
            } else {
                totalPrice = normalPrice
                if (debugMode) {
                    console.group(
                        `return card : ${returnCard?.id} | totalPrice(OW_pr+RT_pr): ${totalPrice} | isSpecialRTPrice: ${false} | OW_pr+RT_pr: ${normalPrice} | OW_splrt+RT_splrt: ${undefined}`
                    )
                }
            }

            returnCard.priceBreakup.multiAirlinePrice =
                totalPrice - (selectedOnwardCard.priceBreakup.multiAirlinePrice || 0)

            if (debugMode) {
                console.log(
                    `return card : ${returnCard?.id} | finalPrice: ${returnCard.priceBreakup.multiAirlinePrice} | selectedOnwardCard.priceBreakup.multiAirlinePrice: ${selectedOnwardCard.priceBreakup.multiAirlinePrice}`
                )
                console.groupEnd()
            }
        })

        flightResultResponseData.sortData.price_up[1] = flightResultResponseData.cards[1]
            .toSorted((a, b) => {
                return (a.priceBreakup.multiAirlinePrice || 0) - (b.priceBreakup.multiAirlinePrice || 0)
            })
            .map(card => {
                return card.id
            })

        if (debugMode) {
            console.log('after sorting return cards (cheapest)', flightResultResponseData.sortData.price_up[1])
        }
    }

    return flightResultResponseData
}

// export const getModifySortedReturnLegCardIds = (cards: any[], selectedJourneyLeg, isIntlRT) => {
//     if (isIntlRT && selectedJourneyLeg === 1 && Array.isArray(cards)) {
//         return cards
//             .toSorted((a, b) => a.priceBreakup.multiAirlinePrice - b.priceBreakup.multiAirlinePrice)
//             .map(card => card.id)
//     }

//     return null
// }
